import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    defaultPageSizes,
    defaultPagination,
    IPagination,
    ISorting,
    LoadStatus,
} from '../../../model';
import { RootState } from '../..';
import {
    loadCustomerUpdates,
    loadCustomerUpdatesFilters,
    loadUnableToNotifyReasons,
} from './customerUpdates.thunks';
import { CustomerWithStatuses } from '../../../model/customerWithUpdates';
import { CustomerUpdatesFilterValues } from './customerUpdates.api';
import {
    customerUpdatesTableColumnIds,
    CustomerUpdatesTableColumnId,
} from '../../../components/CustomerUpdatesPage/CustomerUpdatesTable/CustomerUpdatesTable.utils';
import { CustomerUpdatesType } from './customerUpdates.types';
import { mergeSkipUndefined } from '../../../utils/objectUtils';

export const getEmptyCustomerUpdatesFilters =
    (): CustomerUpdatesFiltersState => ({
        cabins: [],
        loyalties: [],
        baggageStatus: null,
        misconnectedCustomerStatus: null,
        messageStatus: null,
    });

export type CustomerUpdatesViewType = 'All' | CustomerUpdatesType;

export type CustomerNotificationFilterStatus = 'Notified' | 'NotNotified';

export type CustomerItemStatus = 'NotNotified' | 'Notified' | 'UnableToNotify';

export type UnableToNotifyReason = { id: string; displayName: string };

export interface CustomerUpdatesFiltersState {
    cabins: string[];
    loyalties: string[];
    baggageStatus: CustomerNotificationFilterStatus | null;
    misconnectedCustomerStatus: CustomerNotificationFilterStatus | null;
    messageStatus: CustomerNotificationFilterStatus | null;
}

export interface CustomerUpdatesQueryParamsState {
    queryText?: string;
    filters: CustomerUpdatesFiltersState;
}

export interface CustomerUpdates {
    flightTeamId?: string;
    customers: CustomerWithStatuses[];
    availableFilters: CustomerUpdatesFilterValues;
    pagination: IPagination;
    sorting: ISorting<CustomerUpdatesTableColumnId>;
    loadStatus: LoadStatus;
    queryParams: CustomerUpdatesQueryParamsState;
    viewType: CustomerUpdatesViewType;
    unableToNotifyReasons: {
        data: UnableToNotifyReason[];
        loadStatus: LoadStatus;
    };
}

const initialState: CustomerUpdates = {
    loadStatus: 'loading',
    customers: [],
    availableFilters: {
        cabins: [],
        frequentFlyerTiers: [],
    },
    pagination: defaultPagination({ pageSize: defaultPageSizes.large }),
    sorting: {
        sortBy: customerUpdatesTableColumnIds.departureTime,
        sortByDescending: true,
    },
    queryParams: {
        filters: getEmptyCustomerUpdatesFilters(),
    },
    viewType: 'All',
    unableToNotifyReasons: {
        data: [],
        loadStatus: 'loading',
    },
};

export const customerUpdatesSlice = createSlice({
    name: 'customerUpdates',
    initialState,
    reducers: {
        setFlightTeamId: (state, action: PayloadAction<string | undefined>) => {
            const id = action.payload;
            state.flightTeamId = id;

            state.pagination.pageSize = id
                ? defaultPageSizes.max
                : defaultPageSizes.large;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage =
                action.payload < 1 ? 1 : action.payload;
        },
        toggleSort: (state, action: PayloadAction<string>) => {
            if (state.sorting.sortBy === action.payload) {
                state.sorting.sortByDescending =
                    !state.sorting.sortByDescending;
            } else {
                state.sorting.sortBy =
                    action.payload as CustomerUpdatesTableColumnId;
                state.sorting.sortByDescending = false;
            }
        },
        setQueryText: (state, action: PayloadAction<string>) => {
            state.queryParams.queryText = action.payload;
            state.pagination.currentPage = 1;
        },
        updateFilters: (
            state,
            action: PayloadAction<Partial<CustomerUpdatesFiltersState>>
        ) => {
            state.queryParams.filters = mergeSkipUndefined(
                state.queryParams.filters,
                action.payload
            );
            state.pagination.currentPage = 1;
        },
        setViewType: (
            state,
            action: PayloadAction<CustomerUpdatesViewType>
        ) => {
            state.viewType = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadCustomerUpdates.pending, (state) => {
                state.loadStatus = 'loading';
                state.customers = [];
            })
            .addCase(loadCustomerUpdates.fulfilled, (state, action) => {
                state.loadStatus = 'idle';

                const { items, totalCount } = action.payload;

                state.customers = [...items];
                state.pagination = {
                    ...state.pagination,
                    totalCount,
                };
            })
            .addCase(loadCustomerUpdates.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(loadCustomerUpdatesFilters.pending, (state) => {
                state.loadStatus = 'loading';
                state.availableFilters = { cabins: [], frequentFlyerTiers: [] };
            })
            .addCase(loadCustomerUpdatesFilters.fulfilled, (state, action) => {
                state.loadStatus = 'idle';

                const { cabins, frequentFlyerTiers } = action.payload;
                state.availableFilters = {
                    cabins: [...cabins],
                    frequentFlyerTiers: [...frequentFlyerTiers],
                };
            })
            .addCase(loadCustomerUpdatesFilters.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(loadUnableToNotifyReasons.pending, (state) => {
                state.unableToNotifyReasons = {
                    data: [],
                    loadStatus: 'loading',
                };
            })
            .addCase(loadUnableToNotifyReasons.fulfilled, (state, action) => {
                state.unableToNotifyReasons = {
                    data: [...action.payload],
                    loadStatus: 'idle',
                };
            })
            .addCase(loadUnableToNotifyReasons.rejected, (state) => {
                state.unableToNotifyReasons = {
                    data: [],
                    loadStatus: 'failed',
                };
            });
    },
});

export const {
    toggleSort,
    setFlightTeamId,
    setQueryText,
    updateFilters,
    setCurrentPage,
    setViewType,
} = customerUpdatesSlice.actions;

export const customerUpdatesState = (state: RootState) => state.customerUpdates;

export default customerUpdatesSlice.reducer;
